import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import VillaModal from '../../components/admin/VillaModal';

const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

const formatApiError = (err, fallback = 'Request failed') => {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail
            .map(d => {
                const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
                return field ? `${field}: ${d.msg}` : d.msg;
            })
            .join('\n');
    }
    return err.message || fallback;
};

const AdminVillas = () => {
    const [villas, setVillas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingVilla, setEditingVilla] = useState(null);
    const [loadingVillaDetails, setLoadingVillaDetails] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [activeTab, setActiveTab] = useState('villas');
    const [searchTerm, setSearchTerm] = useState('');

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        };
    }, []);

    const loadVillas = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/api/villas?limit=100`, getAuthHeaders());
            setVillas(res.data.villas || []);
        } catch (err) {
            console.error('Error loading villas:', err);
            alert('Error loading villas: ' + formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    const loadBookings = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/villa-bookings`, getAuthHeaders());
            setBookings(res.data || []);
        } catch (err) {
            console.error('Error loading villa bookings:', err);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        loadVillas();
        loadBookings();
    }, [loadVillas, loadBookings]);

    const openAddModal = () => {
        setEditingVilla(null);
        setLoadingVillaDetails(false);
        setModalOpen(true);
    };

    const openEditModal = async (villa) => {
        const villaId = villa.id || villa._id;
        if (!villaId) {
            alert('Property ID is missing — cannot edit.');
            return;
        }

        setModalOpen(true);
        setLoadingVillaDetails(true);
        setEditingVilla(null);

        try {
            const res = await axios.get(`${API_URL}/api/villas/${villaId}`, getAuthHeaders());
            setEditingVilla(res.data);
        } catch (err) {
            console.error('Error loading villa for edit:', err);
            alert('Could not load property details: ' + formatApiError(err));
            setModalOpen(false);
        } finally {
            setLoadingVillaDetails(false);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingVilla(null);
        setLoadingVillaDetails(false);
    };

    const handleSave = async (villaData) => {
        try {
            if (editingVilla) {
                const id = editingVilla.id || editingVilla._id;
                await axios.put(`${API_URL}/api/villas/${id}`, villaData, getAuthHeaders());
                alert('Property updated successfully!');
            } else {
                await axios.post(`${API_URL}/api/villas`, villaData, getAuthHeaders());
                alert('Property created successfully!');
            }
            closeModal();
            loadVillas();
        } catch (err) {
            alert('Error saving villa:\n' + formatApiError(err, 'Failed to save villa'));
        }
    };

    const handleDelete = async (villa) => {
        const id = villa.id || villa._id;
        if (!id) {
            alert('Property ID is missing — cannot delete.');
            return;
        }

        if (!window.confirm(`Hide "${villa.title}" from the public site?\n\nYou can re-activate it later by editing the property.`)) return;

        try {
            await axios.delete(`${API_URL}/api/villas/${id}`, getAuthHeaders());
            loadVillas();
            alert('Property hidden from public site.');
        } catch (err) {
            alert('Error deleting villa:\n' + formatApiError(err, 'Failed to delete villa'));
        }
    };

    const toggleStatus = async (villa) => {
        const id = villa.id || villa._id;
        if (!id) return;

        try {
            await axios.put(
                `${API_URL}/api/villas/${id}`,
                { isActive: !villa.isActive },
                getAuthHeaders()
            );
            loadVillas();
        } catch (err) {
            alert('Error updating status: ' + formatApiError(err));
        }
    };

    const handleBookingStatus = async (bookingId, status) => {
        try {
            await axios.put(`${API_URL}/api/villa-bookings/${bookingId}`, { status }, getAuthHeaders());
            loadBookings();
        } catch (err) {
            alert('Error updating booking status');
        }
    };

    const formatPrice = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price ?? 0);

    const filteredVillas = villas.filter(v =>
        v.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.country?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const statusColors = {
        pending: 'bg-yellow-100 text-yellow-800',
        confirmed: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800',
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Villas & Bungalows</h1>
                    <p className="text-gray-500 mt-1">Manage properties and booking enquiries</p>
                </div>
                <button
                    onClick={openAddModal}
                    className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition transform hover:scale-105"
                >
                    <i className="fas fa-plus mr-2"></i>Add Property
                </button>
            </div>

            <div className="flex border-b mb-6">
                {['villas', 'bookings'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-3 font-medium capitalize transition border-b-2 ${activeTab === tab ? 'border-brand-teal text-brand-teal' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        {tab === 'villas' ? <><i className="fas fa-home mr-2"></i>Properties ({villas.length})</> : <><i className="fas fa-calendar-check mr-2"></i>Booking Enquiries ({bookings.length})</>}
                    </button>
                ))}
            </div>

            {activeTab === 'villas' && (
                <>
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search by title, location or country..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none"
                        />
                    </div>

                    {loading ? (
                        <div className="text-center py-20"><i className="fas fa-spinner fa-spin text-4xl text-brand-teal"></i></div>
                    ) : filteredVillas.length === 0 ? (
                        <div className="text-center py-20 text-gray-500">
                            <i className="fas fa-home text-5xl mb-4 block text-gray-300"></i>
                            No properties found. Click "Add Property" to get started.
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="text-left px-6 py-4 font-semibold text-gray-600">Property</th>
                                        <th className="text-left px-6 py-4 font-semibold text-gray-600">Type</th>
                                        <th className="text-left px-6 py-4 font-semibold text-gray-600">Location</th>
                                        <th className="text-left px-6 py-4 font-semibold text-gray-600">Price/Night</th>
                                        <th className="text-left px-6 py-4 font-semibold text-gray-600">Guests</th>
                                        <th className="text-left px-6 py-4 font-semibold text-gray-600">Status</th>
                                        <th className="text-left px-6 py-4 font-semibold text-gray-600">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {filteredVillas.map(villa => (
                                        <tr key={villa.id || villa._id} className="hover:bg-gray-50 transition">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    {villa.images?.[0] && (
                                                        <img src={villa.images[0].startsWith('/') ? `${API_URL}${villa.images[0]}` : villa.images[0]} alt="" className="w-12 h-12 object-cover rounded-lg" />
                                                    )}
                                                    <div>
                                                        <div className="font-semibold text-gray-800">{villa.title}</div>
                                                        <div className="text-xs text-gray-500">{villa.bedrooms} bed · {villa.bathrooms} bath</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${villa.propertyType === 'villa' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                                                    {villa.propertyType}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600">{villa.location}, {villa.country}</td>
                                            <td className="px-6 py-4 font-semibold text-brand-teal">{formatPrice(villa.pricePerNight)}</td>
                                            <td className="px-6 py-4 text-gray-600">{villa.maxGuests} guests</td>
                                            <td className="px-6 py-4">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleStatus(villa)}
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-colors ${villa.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                                >
                                                    {villa.isActive ? 'Active' : 'Hidden'}
                                                </button>
                                                {villa.isFeatured && <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">Featured</span>}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    <button type="button" onClick={() => openEditModal(villa)} className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition" title="Edit">
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button type="button" onClick={() => handleDelete(villa)} className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition" title="Hide from public site">
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {activeTab === 'bookings' && (
                bookings.length === 0 ? (
                    <div className="text-center py-20 text-gray-500">
                        <i className="fas fa-calendar-times text-5xl mb-4 block text-gray-300"></i>
                        No booking enquiries yet.
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="text-left px-6 py-4 font-semibold text-gray-600">Guest</th>
                                    <th className="text-left px-6 py-4 font-semibold text-gray-600">Property</th>
                                    <th className="text-left px-6 py-4 font-semibold text-gray-600">Dates</th>
                                    <th className="text-left px-6 py-4 font-semibold text-gray-600">Guests</th>
                                    <th className="text-left px-6 py-4 font-semibold text-gray-600">Status</th>
                                    <th className="text-left px-6 py-4 font-semibold text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {bookings.map(b => (
                                    <tr key={b.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4">
                                            <div className="font-semibold text-gray-800">{b.guestName}</div>
                                            <div className="text-xs text-gray-500">{b.email} · {b.phone}</div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{b.villaName}</td>
                                        <td className="px-6 py-4 text-gray-600">{b.checkIn} → {b.checkOut}</td>
                                        <td className="px-6 py-4 text-gray-600">{b.guests}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[b.status] || 'bg-gray-100 text-gray-700'}`}>{b.status}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <select value={b.status} onChange={e => handleBookingStatus(b.id, e.target.value)} className="text-xs border rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-teal">
                                                <option value="pending">Pending</option>
                                                <option value="confirmed">Confirmed</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            <VillaModal
                show={modalOpen}
                onClose={closeModal}
                villa={editingVilla}
                loadingVilla={loadingVillaDetails}
                onSave={handleSave}
            />
        </div>
    );
};

export default AdminVillas;
